import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import "./waiting-screen.css";

interface VideoCallData {
  job_title: string | null;
  interview_schedule: string;
  video_call_data: {
    host: { name: string };
    guest: { name: string };
  };
}

interface WaitingScreenProps {
  videoCallData: VideoCallData;
  counterpartName: string;
  onScheduleReached: () => void;
  showRecordingNotice?: boolean;
}

const formatCountdown = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return [
      hours.toString().padStart(2, "0"),
      minutes.toString().padStart(2, "0"),
      seconds.toString().padStart(2, "0"),
    ].join(":");
  }
  return [
    minutes.toString().padStart(2, "0"),
    seconds.toString().padStart(2, "0"),
  ].join(":");
};

const WaitingScreen: React.FC<WaitingScreenProps> = ({
  videoCallData,
  counterpartName,
  onScheduleReached,
  showRecordingNotice = false,
}) => {
  const getSecondsRemaining = () => {
    const scheduleDate = moment.utc(videoCallData.interview_schedule).local().toDate();
    return Math.max(0, Math.floor((scheduleDate.getTime() - Date.now()) / 1000));
  };

  const [secondsRemaining, setSecondsRemaining] = useState<number>(getSecondsRemaining);
  const initialSecondsRef = useRef(Math.max(secondsRemaining, 1));
  const firedRef = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Keep a ref to the latest callback so the interval never holds a stale closure
  const onScheduleReachedRef = useRef(onScheduleReached);
  useEffect(() => {
    onScheduleReachedRef.current = onScheduleReached;
  }, [onScheduleReached]);

  useEffect(() => {
    // If already at zero on mount, fire immediately
    if (secondsRemaining <= 0) {
      if (!firedRef.current) {
        firedRef.current = true;
        onScheduleReachedRef.current();
      }
      return;
    }

    // Recalculate from wall clock each tick to prevent drift
    intervalRef.current = setInterval(() => {
      const remaining = getSecondsRemaining();
      setSecondsRemaining(remaining);
      if (remaining <= 0 && !firedRef.current) {
        firedRef.current = true;
        if (intervalRef.current) clearInterval(intervalRef.current);
        onScheduleReachedRef.current();
      }
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- interval uses refs, not closures

  const progress = 1 - secondsRemaining / initialSecondsRef.current;
  const circumference = 2 * Math.PI * 54;
  const strokeDashoffset = circumference * (1 - progress);

  const getStrokeColor = () => {
    if (secondsRemaining <= 60) return "#e74c3c";
    if (secondsRemaining <= 300) return "#f39c12";
    return "#3498db";
  };

  const countdownLabel = formatCountdown(secondsRemaining);
  const formattedSchedule = moment
    .utc(videoCallData.interview_schedule)
    .local()
    .format("dddd, MMM D [at] h:mm A");

  return (
    <div className="drjob-ws-container">
      <div className="drjob-ws-card">
        <h2 className="drjob-ws-title">Interview Starts In</h2>

        <div className="drjob-ws-ring-wrapper">
          <svg
            width="160"
            height="160"
            viewBox="0 0 160 160"
            role="img"
            aria-labelledby="drjob-ws-countdown-title"
          >
            <title id="drjob-ws-countdown-title">
              Interview starts in {countdownLabel}
            </title>
            {/* Background track */}
            <circle cx="80" cy="80" r="54" fill="none" stroke="#e8ecf0" strokeWidth="10" />
            {/* Progress arc */}
            <circle
              cx="80"
              cy="80"
              r="54"
              fill="none"
              stroke={getStrokeColor()}
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              transform="rotate(-90 80 80)"
              style={{ transition: "stroke-dashoffset 0.8s ease, stroke 0.5s ease" }}
            />
            {/* Countdown text — dominantBaseline for cross-browser consistency */}
            <text
              x="80"
              y="80"
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="22"
              fontWeight="700"
              fill="#333"
              fontFamily="'Courier New', monospace"
              dy="-8"
            >
              {countdownLabel}
            </text>
            <text
              x="80"
              y="80"
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="12"
              fill="#888"
              dy="14"
            >
              remaining
            </text>
          </svg>
        </div>

        <div className="drjob-ws-info-card">
          {videoCallData.job_title && (
            <div className="drjob-ws-info-row">
              <i className="fa fa-briefcase drjob-ws-info-icon"></i>
              <span className="drjob-ws-info-text">{videoCallData.job_title}</span>
            </div>
          )}
          <div className="drjob-ws-info-row">
            <i className="fa fa-user drjob-ws-info-icon"></i>
            <span className="drjob-ws-info-text">With: {counterpartName}</span>
          </div>
          <div className="drjob-ws-info-row">
            <i className="fa fa-calendar drjob-ws-info-icon"></i>
            <span className="drjob-ws-info-text">{formattedSchedule}</span>
          </div>
        </div>

        <p className="drjob-ws-hint">
          Please check your camera and microphone before the interview begins.
        </p>
        {showRecordingNotice && (
          <div className="drjob-ws-recording-notice">
            <i className="fa fa-exclamation-triangle"></i>
            <span>
              <strong>Recording Notice:</strong> This interview is not automatically recorded by the system.
              Please ensure you record the session manually from your end if a recording is needed.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default WaitingScreen;
